import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PHONE_QUERY, TOUCH_QUERY, activatable, useMediaQuery } from '../ui';
import {
  TimelineMachine,
  TimelineSegment,
  effectiveRemainingSeconds,
  formatAxisTime,
  formatCycleLabel,
  segmentCycleLabel,
  formatDuration,
  formatRemainingClock,
  formatSegmentLabelWithCycle,
  formatTimelineErrorDetail,
  formatTimelineStatus,
  getTimelineStatusCategory,
  machineKey,
  machineLiveTooltip,
  toMs,
} from './statusTimelineUtils';

type Props = {
  machines: TimelineMachine[];
  segments: TimelineSegment[];
  viewStartMs: number;
  viewEndMs: number;
  onViewChange: (startMs: number, endMs: number) => void;
  windowStartMs: number;
  windowEndMs: number;
  onRowContextMenu?: (row: TimelineMachine, x: number, y: number) => void;
};

const LABEL_WIDTH = 118;
const LABEL_WIDTH_PHONE = 86;
const ROW_HEIGHT = 44;
/** Finger travel below this is a tap, not a pan. */
const TAP_SLOP_PX = 6;
const TAP_OPEN_DELAY_MS = 100;
const TAP_CLICK_WINDOW_MS = 500;
const AXIS_HEIGHT = 28;
const MIN_LABEL_WIDTH_PX = 90;
const VIRTUALIZE_THRESHOLD = 40;
const OVERSCAN = 6;

const StatusTimelineChart: React.FC<Props> = ({
  machines,
  segments,
  viewStartMs,
  viewEndMs,
  onViewChange,
  windowStartMs,
  windowEndMs,
  onRowContextMenu,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(600);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(420);
  const dragRef = useRef<{ x: number; start: number; end: number } | null>(null);
  const draggedRef = useRef(false);
  const lastTapAtRef = useRef(0);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const isTouch = useMediaQuery(TOUCH_QUERY);
  const labelWidth = isPhone ? LABEL_WIDTH_PHONE : LABEL_WIDTH;

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setTrackWidth(w);
    });
    ro.observe(el);
    setTrackWidth(el.clientWidth || 600);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h && h > 0) setViewportHeight(h);
    });
    ro.observe(el);
    setViewportHeight(el.clientHeight || 420);
    return () => ro.disconnect();
  }, []);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const spanMs = Math.max(viewEndMs - viewStartMs, 1000);
  const rows = machines;
  const useVirtual = rows.length > VIRTUALIZE_THRESHOLD;

  const segmentsByRow = useMemo(() => {
    const map = new Map<string, TimelineSegment[]>();
    const now = Date.now();
    const clampEnd = (end: number | null) => {
      if (end == null) return Math.min(now, windowEndMs);
      return end;
    };

    const macToName = new Map<string, string>();
    rows.forEach((row) => {
      if (row.mac_id) macToName.set(row.mac_id, row.name);
    });

    rows.forEach((row) => {
      const key = row.name;
      const rowSegs = segments.filter((s) => {
        const segName = (s.Machine || '').trim();
        if (segName && segName === row.name) return true;
        if (s.mac_id && row.mac_id && s.mac_id === row.mac_id) return true;
        if (s.mac_id && macToName.get(s.mac_id) === row.name) return true;
        return false;
      });
      map.set(
        key,
        rowSegs
          .map((s) => ({ ...s }))
          .filter((s) => {
            const start = toMs(s.start_time);
            if (start == null) return false;
            const end = clampEnd(toMs(s.end_time));
            return end > viewStartMs && start < viewEndMs;
          })
      );
    });
    return map;
  }, [rows, segments, viewStartMs, viewEndMs, windowEndMs]);

  const msToX = useCallback(
    (ms: number) => ((ms - viewStartMs) / spanMs) * trackWidth,
    [viewStartMs, spanMs, trackWidth]
  );

  const nowX =
    nowMs >= viewStartMs && nowMs <= viewEndMs ? msToX(nowMs) : null;

  const axisTicks = useMemo(() => {
    const count = Math.min(8, Math.max(4, Math.floor(trackWidth / 100)));
    const ticks: number[] = [];
    for (let i = 0; i <= count; i++) {
      ticks.push(viewStartMs + (spanMs * i) / count);
    }
    return ticks;
  }, [viewStartMs, spanMs, trackWidth]);

  const zoomAround = useCallback(
    (factor: number, anchorX: number) => {
      const anchorMs = viewStartMs + (anchorX / trackWidth) * spanMs;
      const newSpan = Math.min(
        windowEndMs - windowStartMs,
        Math.max(10 * 1000, spanMs * factor)
      );
      let newStart = anchorMs - (anchorX / trackWidth) * newSpan;
      let newEnd = newStart + newSpan;
      if (newStart < windowStartMs) {
        newStart = windowStartMs;
        newEnd = newStart + newSpan;
      }
      if (newEnd > windowEndMs) {
        newEnd = windowEndMs;
        newStart = newEnd - newSpan;
      }
      onViewChange(newStart, newEnd);
    },
    [viewStartMs, trackWidth, spanMs, windowStartMs, windowEndMs, onViewChange]
  );

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rect = trackRef.current?.getBoundingClientRect();
      const x = rect ? e.clientX - rect.left : trackWidth / 2;
      zoomAround(e.deltaY > 0 ? 1.15 : 1 / 1.15, x);
    },
    [zoomAround, trackWidth]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      dragRef.current = { x: e.clientX, start: viewStartMs, end: viewEndMs };
      draggedRef.current = false;
    },
    [viewStartMs, viewEndMs]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.x;
      if (Math.abs(dx) > TAP_SLOP_PX) draggedRef.current = true;
      const deltaMs = -(dx / trackWidth) * (dragRef.current.end - dragRef.current.start);
      let newStart = dragRef.current.start + deltaMs;
      let newEnd = dragRef.current.end + deltaMs;
      const span = newEnd - newStart;
      if (newStart < windowStartMs) {
        newStart = windowStartMs;
        newEnd = newStart + span;
      }
      if (newEnd > windowEndMs) {
        newEnd = windowEndMs;
        newStart = newEnd - span;
      }
      onViewChange(newStart, newEnd);
    },
    [trackWidth, windowStartMs, windowEndMs, onViewChange]
  );

  const onPointerCancel = useCallback(() => {
    dragRef.current = null;
  }, []);

  const openAfterTap = useCallback(
    (row: TimelineMachine, x: number, y: number) => {
      lastTapAtRef.current = Date.now();
      window.setTimeout(() => onRowContextMenu?.(row, x, y), TAP_OPEN_DELAY_MS);
    },
    [onRowContextMenu]
  );

  // Touch has no right-click, so a tap on the bars opens the same actions. This
  // reads pointerup rather than click: Chrome swallows the click for a tap just
  // after a fast swipe (it assumes the tap is stopping scroll momentum), which
  // would make taps vanish straight after panning. A vertical scroll ends in
  // pointercancel instead, and a mouse click stays a pan.
  //
  // When the click does arrive it follows pointerup within milliseconds, so the
  // sheet opens after a short delay: opened immediately, that click would land on
  // the sheet's backdrop and close it again.
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const wasPressed = dragRef.current != null;
      dragRef.current = null;
      if (!wasPressed || draggedRef.current || e.pointerType === 'mouse' || !onRowContextMenu) return;
      const name = (e.target as HTMLElement).closest<HTMLElement>('[data-machine]')?.dataset.machine;
      const row = machines.find((m) => m.name === name);
      if (row) openAfterTap(row, e.clientX, e.clientY);
    },
    [machines, onRowContextMenu, openAfterTap]
  );

  const handleRowContextMenu = (row: TimelineMachine, e: React.MouseEvent) => {
    if (!onRowContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    onRowContextMenu(row, e.clientX, e.clientY);
  };


  const visibleRange = useMemo(() => {
    if (!useVirtual) {
      return { start: 0, end: rows.length, offsetY: 0, totalHeight: rows.length * ROW_HEIGHT };
    }
    const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
    const end = Math.min(rows.length, start + visibleCount);
    return {
      start,
      end,
      offsetY: start * ROW_HEIGHT,
      totalHeight: rows.length * ROW_HEIGHT,
    };
  }, [useVirtual, scrollTop, viewportHeight, rows.length]);

  const visibleRows = rows.slice(visibleRange.start, visibleRange.end);

  const machinesByName = useMemo(() => {
    const map = new Map<string, TimelineMachine>();
    rows.forEach((row) => {
      const name = (row.name || '').trim();
      if (name) map.set(name, row);
    });
    return map;
  }, [rows]);

  const renderSegments = (key: string) => {
    const rowSegs = segmentsByRow.get(key) || [];
    const machine = machinesByName.get(key);
    return rowSegs.map((seg, idx) => {
      const start = toMs(seg.start_time)!;
      // Full event end for labels/tooltips (open = now); visual bar still clips to view.
      const eventEnd = toMs(seg.end_time) ?? Date.now();
      const left = msToX(Math.max(start, viewStartMs));
      const right = msToX(Math.min(eventEnd, viewEndMs));
      const width = Math.max(right - left, 2);
      const category = getTimelineStatusCategory(seg.statusName);
      const durationText = formatDuration(eventEnd - start);
      const showLabel = width >= MIN_LABEL_WIDTH_PX;
      const cycleLabel = segmentCycleLabel(seg, machine);
      const errorDetail =
        category === 'error' ? formatTimelineErrorDetail(seg) : null;
      const label = formatSegmentLabelWithCycle(
        seg.statusName,
        start,
        eventEnd,
        cycleLabel,
        errorDetail
      );
      const endLabel = seg.end_time ? new Date(eventEnd).toLocaleString() : 'now';
      const tooltipLines = [
        formatTimelineStatus(seg.statusName),
        ...(errorDetail ? [`Error: ${errorDetail}`] : []),
        `Duration: ${durationText}`,
        ...(cycleLabel ? [`Cycle: ${cycleLabel}`] : []),
        `${new Date(start).toLocaleString()} → ${endLabel}`,
      ];
      return (
        <div
          key={`${key}-${idx}-${start}`}
          className={`status-timeline-segment cat-${category}`}
          style={{ left, width }}
          title={tooltipLines.join('\n')}
        >
          {showLabel && (
            <span className="status-timeline-segment-label">{label}</span>
          )}
        </div>
      );
    });
  };

  return (
    <div className="status-timeline-chart">
      <div className="status-timeline-zoom-controls">
        <button
          type="button"
          onClick={() => zoomAround(1 / 1.4, trackWidth / 2)}
          title="Zoom in"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => zoomAround(1.4, trackWidth / 2)}
          title="Zoom out"
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          onClick={() => onViewChange(windowStartMs, windowEndMs)}
          title="Reset zoom"
        >
          Reset
        </button>
        <span className="status-timeline-zoom-hint">
          {isTouch ? 'Drag sideways to pan' : 'Ctrl+scroll to zoom · drag to pan'}
        </span>
      </div>

      <div
        className="status-timeline-scroll"
        ref={scrollRef}
        onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
      >
        <div
          className="status-timeline-body"
          style={useVirtual ? { height: visibleRange.totalHeight + AXIS_HEIGHT } : undefined}
        >
          <div
            className="status-timeline-labels"
            style={{
              width: labelWidth,
              ...(useVirtual
                ? { paddingTop: visibleRange.offsetY, height: visibleRange.totalHeight + AXIS_HEIGHT }
                : {}),
            }}
          >
            {visibleRows.map((row) => {
              const remaining = effectiveRemainingSeconds(row, nowMs);
              const cycle = formatCycleLabel(row);
              const metaParts: string[] = [];
              if (remaining != null && remaining > 0) {
                metaParts.push(formatRemainingClock(remaining));
              }
              if (cycle) metaParts.push(cycle);
              const meta = metaParts.join(' · ');
              return (
                <div
                  key={machineKey(row.mac_id, row.name)}
                  className={`status-timeline-label${row.is_door_open === true ? ' door-open' : ''}`}
                  style={{ height: ROW_HEIGHT }}
                  title={machineLiveTooltip(row)}
                  role="button"
                  aria-haspopup="menu"
                  aria-label={`${row.name} actions`}
                  onContextMenu={(e) => handleRowContextMenu(row, e)}
                  {...activatable(() => {
                    if (!onRowContextMenu) return;
                    const rect = document.activeElement?.getBoundingClientRect();
                    onRowContextMenu(row, rect?.right ?? 0, rect?.top ?? 0);
                  })}
                  onPointerUp={(e) => {
                    if (e.pointerType !== 'mouse') openAfterTap(row, e.clientX, e.clientY);
                  }}
                  onClick={(e) => {
                    // A touch already opened the sheet from pointerup.
                    if (Date.now() - lastTapAtRef.current < TAP_CLICK_WINDOW_MS) return;
                    handleRowContextMenu(row, e);
                  }}
                >
                  <span className="status-timeline-label-name">
                    {row.name}
                    {row.is_door_open === true && (
                      <span className="status-timeline-door" aria-label="Door open">
                        door
                      </span>
                    )}
                  </span>
                  {meta ? (
                    <span className="status-timeline-label-meta">{meta}</span>
                  ) : null}
                </div>
              );
            })}
            {!useVirtual && (
              <div className="status-timeline-label axis-spacer" style={{ height: AXIS_HEIGHT }} />
            )}
          </div>

          <div
            className="status-timeline-track-wrap"
            ref={trackRef}
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            style={
              useVirtual
                ? { paddingTop: visibleRange.offsetY, minHeight: visibleRange.totalHeight + AXIS_HEIGHT }
                : undefined
            }
          >
            {visibleRows.map((row) => {
              const key = row.name;
              return (
                <div
                  key={machineKey(row.mac_id, row.name)}
                  className="status-timeline-row"
                  data-machine={row.name}
                  style={{ height: ROW_HEIGHT }}
                  onContextMenu={(e) => handleRowContextMenu(row, e)}
                >
                  {renderSegments(key)}
                </div>
              );
            })}

            <div
              className="status-timeline-axis"
              style={{
                height: AXIS_HEIGHT,
                ...(useVirtual
                  ? { position: 'absolute', bottom: 0, left: 0, right: 0 }
                  : {}),
              }}
            >
              {axisTicks.map((t) => (
                <div key={t} className="status-timeline-tick" style={{ left: msToX(t) }}>
                  <span>{formatAxisTime(t, spanMs)}</span>
                </div>
              ))}
            </div>

            {nowX != null && (
              <div className="status-timeline-now-marker" style={{ left: nowX }}>
                <div className="status-timeline-now-line" />
                <span className="status-timeline-now-label">now</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="status-timeline-legend">
        <span className="legend-item">
          <i className="swatch cat-available" /> Available
        </span>
        <span className="legend-item">
          <i className="swatch cat-ready" /> Ready to start
        </span>
        <span className="legend-item">
          <i className="swatch cat-in-use" /> In use
        </span>
        <span className="legend-item">
          <i className="swatch cat-complete" /> Complete
        </span>
        <span className="legend-item">
          <i className="swatch cat-error" /> Error / network / unavailable
        </span>
        <span className="legend-item">
          <i className="swatch cat-out-of-order" /> Out of order
        </span>
        <span className="legend-item">
          <i className="swatch cat-unknown" /> Unknown
        </span>
      </div>
    </div>
  );
};

export default StatusTimelineChart;
